import { useMemo } from 'react';
import { usePERentStore } from '../store/usePERentStore';

export interface PERentLineItem {
  key: string;
  label: string;
  monthlyEuro: number;
  included: boolean;
  isDeduction?: boolean;
}

export interface PERentCalculationParams {
  lines: PERentLineItem[];
  salonTeamHoursMonthly: number;
}

export interface PERentCalculationResult {
  rentBaseMonthly: number;
  excludedMonthly: number;
  totalBwaMonthly: number;
  tenantHours: number;
  salonHours: number;
  totalHours: number;
  hasTenantHours: boolean;
  /** Kalkulierter Stundenfaktor = Kosten ÷ (Team + Mieter) — nur mit Mieterstunden */
  calculatedHourlyFactor: number;
  /** Effektiver Faktor (Override oder kalkuliert) */
  hourlyFactor: number;
  isHourlyFactorOverridden: boolean;
  rentNet: number;
  rentGross: number;
  calculatedRentNet: number;
}

export const usePERentCalculation = ({
  lines,
  salonTeamHoursMonthly,
}: PERentCalculationParams): PERentCalculationResult => {
  const tenantHours = usePERentStore((s) => s.tenantMonthlyHours);
  const hourlyFactorOverride = usePERentStore((s) => s.hourlyFactorOverride);

  return useMemo(() => {
    const totalBwaMonthly = lines.reduce((s, l) => s + (l.monthlyEuro || 0), 0);
    const rentBaseMonthly = lines
      .filter((l) => l.included)
      .reduce((s, l) => s + (l.monthlyEuro || 0), 0);
    const excludedMonthly = Number((totalBwaMonthly - rentBaseMonthly).toFixed(2));

    const salonHours = Math.round(Number(salonTeamHoursMonthly) || 0);
    const tenant = Number(tenantHours) || 0;
    const hasTenantHours = tenant > 0;
    const rentBase = Math.max(0, Math.round(rentBaseMonthly));
    const totalHours = hasTenantHours
      ? Number((salonHours + tenant).toFixed(2))
      : salonHours;

    // Stundenfaktor erst sinnvoll, wenn Mieterstunden da sind: Kosten ÷ (Team + Mieter)
    const calculatedHourlyFactor =
      hasTenantHours && totalHours > 0
        ? Number((rentBase / totalHours).toFixed(2))
        : 0;

    // 0 / null = kein Override → wieder kalkulierter Stundenfaktor
    const isHourlyFactorOverridden =
      hasTenantHours &&
      hourlyFactorOverride !== null &&
      !Number.isNaN(hourlyFactorOverride) &&
      hourlyFactorOverride > 0;

    const hourlyFactor = isHourlyFactorOverridden
      ? Number(Number(hourlyFactorOverride).toFixed(2))
      : calculatedHourlyFactor;

    const rentNet = hasTenantHours ? Number((hourlyFactor * tenant).toFixed(2)) : 0;
    const rentGross = Number((rentNet * 1.19).toFixed(2));
    const calculatedRentNet = hasTenantHours
      ? Number((calculatedHourlyFactor * tenant).toFixed(2))
      : 0;

    return {
      rentBaseMonthly: rentBase,
      excludedMonthly: Math.round(excludedMonthly),
      totalBwaMonthly: Math.round(totalBwaMonthly),
      tenantHours: tenant,
      salonHours,
      totalHours,
      hasTenantHours,
      calculatedHourlyFactor,
      hourlyFactor,
      isHourlyFactorOverridden,
      rentNet,
      rentGross,
      calculatedRentNet,
    };
  }, [lines, salonTeamHoursMonthly, tenantHours, hourlyFactorOverride]);
};
